/**
 * WastewaterCommunityShareChart
 *
 * Simple doughnut chart showing wastewater's share of the total
 * community GHG inventory. Accepts a pre-built sectors array with
 * exactly two entries: [Wastewater, All Other Sectors].
 */

import React, { useCallback, useEffect, useRef } from 'react';
import { Chart, registerables } from 'chart.js';

Chart.register(...registerables);

export interface SectorEmissions {
  label: string;
  mtco2e: number;
  color: string;
}

interface WastewaterCommunityShareChartProps {
  sectors: SectorEmissions[];
  year: number;
  location: string;
}

const WastewaterCommunityShareChart: React.FC<WastewaterCommunityShareChartProps> = ({
  sectors,
  year,
  location,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chartRef = useRef<Chart | null>(null);

  const destroyChart = useCallback(() => {
    if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null; }
    if (canvasRef.current) {
      try { const e = Chart.getChart(canvasRef.current); if (e) e.destroy(); } catch {}
    }
  }, []);

  useEffect(() => {
    if (!canvasRef.current || sectors.length === 0) return;
    destroyChart();
    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;

    const total = sectors.reduce((sum, s) => sum + s.mtco2e, 0);

    chartRef.current = new Chart(ctx, {
      type: 'doughnut',
      data: {
        labels: sectors.map((s) => s.label),
        datasets: [
          {
            data: sectors.map((s) => s.mtco2e),
            backgroundColor: sectors.map((s) => s.color),
            borderColor: '#fff',
            borderWidth: 2,
            hoverBorderWidth: 3,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: true,
        cutout: '60%',
        plugins: {
          title: {
            display: true,
            text: `Wastewater Share of Community GHG Inventory — ${location} (${year})`,
            font: { size: 14, weight: 'bold' },
            color: '#1f2937',
            padding: { bottom: 12 },
          },
          legend: {
            display: true,
            position: 'bottom',
            labels: { boxWidth: 14, font: { size: 12 }, color: '#374151' },
          },
          tooltip: {
            callbacks: {
              label: (ctx) => {
                const v = ctx.raw as number;
                const share = total > 0 ? ((v / total) * 100).toFixed(1) : '?';
                return `${ctx.label}: ${v.toLocaleString('en-US', { maximumFractionDigits: 0 })} MTCO₂e (${share}%)`;
              },
            },
          },
        },
      },
    });
    return () => destroyChart();
  }, [sectors, year, location, destroyChart]);

  if (sectors.length === 0) {
    return (
      <div style={{ padding: 32, color: '#6b7280', textAlign: 'center' }}>
        Community GHG inventory total not available for this jurisdiction.
      </div>
    );
  }

  const ww = sectors.find((s) => s.label === 'Wastewater');
  const total = sectors.reduce((sum, s) => sum + s.mtco2e, 0);
  const share = ww && total > 0 ? ((ww.mtco2e / total) * 100).toFixed(1) : null;

  return (
    <div>
      <canvas ref={canvasRef} style={{ maxHeight: 320 }} />
      {share && (
        <p style={{ textAlign: 'center', marginTop: 8, fontSize: 13, color: '#6b7280' }}>
          Wastewater = {share}% of total community GHG inventory ({year})
        </p>
      )}
    </div>
  );
};

export default WastewaterCommunityShareChart;
